/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {MouseEventHandler} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import Render from '@ion/dls/src/render/Render';
import SystemFullScreenEnterIcon from '../../../icons/current/SystemFullScreenEnterIcon';
import SystemFullScreenExitIcon from '../../../icons/current/SystemFullScreenExitIcon';
import {dlsColor} from '../../../theme/theme';
import useThemeContext from '../../../theme/useThemeContext';
import {IconSize} from '../../../types/types';
import {IconButton} from '../../buttons/IconButton';
import FullScreen from '../../videoplayer/FullScreen';
import PlayPause from '../../videoplayer/PlayPause';
import Volume from '../../videoplayer/Volume';

export interface VisionPlayerControlsProps {
  playing: boolean;
  restart?: boolean;
  volume: number;
  audio?: boolean;
  fullScreen: boolean;
  label?: string;
  onPlayPause: MouseEventHandler<HTMLButtonElement>;
  onVolumeIcon: MouseEventHandler<HTMLButtonElement>;
  onVolumeChange: Function;
  onClickFullscreen: MouseEventHandler<HTMLButtonElement>;
}

const useStyles = makeStyles(() => ({
  bar : {
    boxSizing       : 'border-box',
    display         : 'flex',
    alignItems      : 'center',
    justifyContent  : 'space-between',
    width           : '100%',
    height          : '52px',
    padding         : '4px 16px',
    gap             : '10px',
    backgroundColor : dlsColor('grey6'),
    borderRadius    : '0px 0px 12px 12px'
  },
  playGroup : {
    display    : 'flex',
    alignItems : 'center',
    gap        : '8px'
  },
  label : {
    fontFamily : 'Inter',
    fontWeight : 500,
    fontSize   : '12px',
    lineHeight : '16px',
    color      : dlsColor('grey1')
  },
  controlsGroup : {
    display    : 'flex',
    alignItems : 'center',
    gap        : '8px'
  }
}));

/**
 * VisionPlayerControls renders the current theme's restyled Vision player
 * control bar - a play/pause control on the left and volume + fullscreen
 * controls on the right. Play/pause and volume reuse the existing
 * `PlayPause`/`Volume` controls from `@ion/dls/src/components/videoplayer`
 * as-is, so none of their click behavior is reimplemented here - only
 * fullscreen renders its own icon (`SystemFullScreenEnterIcon`/
 * `SystemFullScreenExitIcon`) rather than the shared `FullScreen` control's
 * icons, since that control is also used by legacy theme and isn't
 * current-theme-only. `label`, when supplied, is rendered next to the play
 * control (e.g. a caller-translated 'Play'/'Pause' string) - this component
 * does not localize any text itself.
 *
 * ```typescript
 *
 *   import {VisionPlayerControls} from
 *     '@ion/dls/src/components/vision/current/VisionPlayerControls';
 *   ...
 *
 *   <VisionPlayerControls playing={playing}
 *     restart={restart}
 *     volume={volume}
 *     audio
 *     fullScreen={fullScreen}
 *     label={playing ? 'Pause' : 'Play'}
 *     onPlayPause={onPlayPause}
 *     onVolumeIcon={onVolumeIcon}
 *     onVolumeChange={onVolumeChange}
 *     onClickFullscreen={onClickFullscreen} />
 *
 * ```
 *
 */
export const VisionPlayerControls = ({
  playing,
  restart = false,
  volume,
  audio = false,
  fullScreen,
  label,
  onPlayPause,
  onVolumeIcon,
  onVolumeChange,
  onClickFullscreen
}: VisionPlayerControlsProps) => {
  const classes                     = useStyles();
  const {isCurrent: isCurrentTheme} = useThemeContext();

  return (
    <div className={classes.bar}>
      <div className={classes.playGroup}>
        <PlayPause size={IconSize.small}
          playing={playing}
          restart={restart}
          handlePlayPause={onPlayPause}
          isCondensedVariant />
        <Render.If condition={isCurrentTheme && !!label}>
          <span className={classes.label}>
            {label}
          </span>
        </Render.If>
      </div>
      <div className={classes.controlsGroup}>
        <Render.If condition={audio}>
          <Volume volume={volume}
            size={IconSize.medium}
            handleVolumeIcon={onVolumeIcon}
            handleVolumeChange={onVolumeChange}
            displayRange={false} />
        </Render.If>
        <Render>
          <Render.If condition={isCurrentTheme}>
            <IconButton onClick={onClickFullscreen}
              icon={
                fullScreen
                  ? <SystemFullScreenExitIcon size={IconSize.medium} />
                  : <SystemFullScreenEnterIcon size={IconSize.medium} />
              } />
          </Render.If>
          <Render.Else>
            <FullScreen size={IconSize.medium}
              fullScreen={fullScreen}
              handleClickFullscreen={onClickFullscreen} />
          </Render.Else>
        </Render>
      </div>
    </div>
  );
};

export default React.memo(VisionPlayerControls);
